import {
  ChoiceList,
  IndexFilters,
  IndexFiltersMode,
  IndexFiltersProps,
  IndexTable,
  InlineStack,
  Layout,
  LegacyCard,
  Text,
  Thumbnail,
  Tooltip,
  useSetIndexFiltersMode,
} from "@shopify/polaris";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "src/components/shopify/PageLayout";
import { Sales_Status, useSalesDetailBySalesStatusQuery } from "src/graphql/codegen_graphql";
import { PricesToolTips } from "src/screens/applications/products/item/upload/PricesToolTips";

export const FormatSaleList = [
  {
    displayName: "Item",
    fieldMatch: "item",
    customeField: true,
  },
  {
    displayName: "Stock",
    fieldMatch: "skuStock",
    customeField: false,
  },
  {
    displayName: "SKU",
    fieldMatch: "sku",
    customeField: true,
  },
  {
    displayName: "Sold (SOP)",
    fieldMatch: "fullPrice",
    customeField: true,
  },
  {
    displayName: "Discount",
    fieldMatch: "discount",
    customeField: true,
  },
  {
    displayName: "Delivery Fee",
    fieldMatch: "deliveryFee",
    customeField: true,
  },
  {
    displayName: "Handling Fee",
    fieldMatch: "handlingFee",
    customeField: true,
  },
  {
    displayName: "Commission",
    fieldMatch: "commission",
    customeField: true,
  },
  {
    displayName: "Net Sale",
    fieldMatch: "netSale",
    customeField: true,
  },
  {
    displayName: "Package",
    fieldMatch: "log",
    customeField: true,
  },
];

export default function (props: any) {
  const { t } = useTranslation();
  const param = new URLSearchParams(props.history.location.search);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);

  const [active, setActive] = useState<string[]>(FormatSaleList.map((x) => x.fieldMatch));

  const { data, loading } = useSalesDetailBySalesStatusQuery({
    fetchPolicy: "no-cache",
    variables: {
      offset: offset * Number(limit),
      limit: Number(limit),
      salesStatus: param.get("type") ? (param.get("type") as Sales_Status) : Sales_Status.All,
      day: Number(param.get("day")) ? Number(param.get("day")) : undefined,
      startDate: param.get("start") ? param.get("start") : undefined,
      endDate: param.get("end") ? param.get("end") : undefined,
    },
  });

  const filters = [
    {
      key: "show",
      label: "Show Column",
      filter: (
        <ChoiceList
          choices={FormatSaleList.filter((x) => !["ITEM", "BRAND", "VENDOR"].includes(x.fieldMatch)).map((x) => {
            return {
              label: x.displayName,
              value: x.fieldMatch,
            };
          })}
          selected={isEmpty(active) ? [] : active.filter((f) => !["ITEM", "BRAND", "VENDOR"].includes(f))}
          title
          titleHidden
          allowMultiple={true}
          onChange={(v) => {
            setActive(["ITEM", "BRAND", "VENDOR", ...v]);
          }}
        />
      ),
      pinned: true,
      shortcut: true,
    },
  ];

  const appliedFilter: IndexFiltersProps["appliedFilters"] = [];

  if (
    active.filter((f) => !["ITEM", "BRAND", "VENDOR"].includes(f)) &&
    !isEmpty(active.filter((f) => !["ITEM", "BRAND", "VENDOR"].includes(f)))
  ) {
    const key = "show";
    appliedFilter.push({
      key,
      label: disambiguateLabel(key, active),
      onRemove: () => {
        setActive(FormatSaleList.map((x) => x.fieldMatch));
      },
    });
  }

  const HeaderToolTip = (header: string) => {
    if (header === "Sold (SOP)") {
      return (
        <PricesToolTips messageId={2}>
          <span>
            {header}
            <b> (?)</b>{" "}
          </span>
        </PricesToolTips>
      );
    }

    if (header === "Discount") {
      return (
        <Tooltip
          content={
            <InlineStack gap="200">
              Discount is the amount (a) you reduce from the original price (SOP). (b) the amount that is reduced by
              marketing campaigns like Promo Code or Promo Combo.
            </InlineStack>
          }
          width="wide"
        >
          <span>
            {header}
            <b> (?)</b>{" "}
          </span>
        </Tooltip>
      );
    }
    if (header === "Delivery Fee") {
      return (
        <Tooltip
          content={
            <InlineStack gap="200">
              DF: Delivery fees include (a) the fee for picking up your product at your site and (b) the delivery fee
              you pay for the buyer
            </InlineStack>
          }
          width="wide"
        >
          <span>
            {header}
            <b> (?)</b>{" "}
          </span>
        </Tooltip>
      );
    }

    if (header === "Handling Fee") {
      return (
        <Tooltip
          content={<InlineStack gap="200">HF: Handling fee is for picking and packing the item.</InlineStack>}
          width="wide"
        >
          <span>
            {header}
            <b> (?)</b>{" "}
          </span>
        </Tooltip>
      );
    }

    if (header === "Commission") {
      return (
        <Tooltip
          content={
            <InlineStack gap="200">
              CM: Commissions are for (a) the platform technology and coordination and (b) partner operation services
              such as sales/aftersales support
            </InlineStack>
          }
          width="wide"
        >
          <span>
            {header}
            <b> (?)</b>{" "}
          </span>
        </Tooltip>
      );
    }

    if (header === "Net Sale") {
      return (
        <Tooltip
          content={
            <InlineStack gap="200">Net Sale = Sold (SOP) - (Delivery Fee + Handling Fee + Commission)</InlineStack>
          }
          width="wide"
        >
          <span>
            {header}
            <b> (?)</b>{" "}
          </span>
        </Tooltip>
      );
    }

    return header;
  };

  return (
    <PageLayout
      pageProp={{
        title: "Sale Summary",
        fullWidth: true,
      }}
      loading={loading}
    >
      <Layout>
        <Layout.Section>
          <LegacyCard>
            <LegacyCard.Section flush>
              <div>
                <IndexFilters
                  filters={filters}
                  mode={mode}
                  onClearAll={() => {
                    setActive(FormatSaleList.map((x) => x.fieldMatch));
                    // setFilter({ ...filter, groupBy: "ITEM" });
                  }}
                  onQueryChange={(value) => {
                    // setSearchInput(value);
                    // if (value === "") {
                    //   setFilter({ ...filter, search: "" });
                    // }
                  }}
                  onQueryClear={() => {
                    // setFilter({ ...filter, search: "" });
                  }}
                  selected={0}
                  setMode={setMode}
                  tabs={[]}
                  queryPlaceholder={t("search_press_enter")}
                  // queryValue={searchInput}
                  hideQueryField
                  appliedFilters={appliedFilter}
                />
              </div>
            </LegacyCard.Section>
            <LegacyCard.Section flush>
              <IndexTable
                headings={
                  isEmpty(active)
                    ? [{ title: "" }]
                    : (FormatSaleList.filter((x) => active.includes(x.fieldMatch)).map((x) => {
                        return {
                          title: HeaderToolTip(x.displayName),
                        };
                      }) as any)
                }
                itemCount={data?.salesDetailBySalesStatus?.data?.length || 0}
                selectable={false}
                loading={loading}
                pagination={{
                  label: (
                    <div className="ml-2">{`${Number(offset * limit) + 1} - ${
                      Number(offset * limit) + Number(limit)
                    } ${t("of")} ${data?.salesDetailBySalesStatus?.page?.total || 0} ${t("records")}`}</div>
                  ) as any,
                  hasNext: (data?.salesDetailBySalesStatus?.data?.length || 0) === Number(limit),
                  hasPrevious: offset > 0,
                  onNext: () => setOffset(offset + 1),
                  onPrevious: () => setOffset(offset - 1),
                  nextTooltip: `${t("tooltip_next_page")} ${Number(offset * limit) + Number(limit) + 1}`,
                  previousTooltip: `${t("tooltip_previous_page")} ${Number(offset * limit) + 1 - Number(limit)}`,
                }}
              >
                {data &&
                  data.salesDetailBySalesStatus?.data?.map((x, i) => {
                    return (
                      <IndexTable.Row key={i} id={i + ""} position={i}>
                        {FormatSaleList.filter((x) => active.includes(x.fieldMatch)).map((head, j) => {
                          if (!!head.customeField && head.fieldMatch === "item") {
                            return (
                              <IndexTable.Cell key={j}>
                                <div
                                  style={{
                                    display: "flex",
                                  }}
                                >
                                  <Thumbnail source={x?.picture || ""} alt="" size="small" />
                                  <div style={{ width: 150 }} className="ml-2">
                                    <Text as="strong" variant="bodySm" truncate>
                                      {x?.uuid}
                                    </Text>
                                    <a
                                      href={"https://www.l192.com/product/" + x?.itemID}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <small>Code: {x?.code}</small>
                                    </a>
                                    {x?.fbl && (
                                      <div>
                                        <small className="text-success">FBL</small>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </IndexTable.Cell>
                            );
                          }

                          if (!!head.customeField && head.fieldMatch === "sku") {
                            return (
                              <IndexTable.Cell key={j}>
                                <div>
                                  <small>Color: {x?.color}</small>
                                  <br />
                                  <small>Size: {x?.size}</small>
                                </div>
                              </IndexTable.Cell>
                            );
                          }

                          if (!!head.customeField && head.fieldMatch === "fullPrice") {
                            return (
                              <IndexTable.Cell key={j}>
                                <Text as="p" variant="bodySm" tone="magic">
                                  ${x ? x.fullPrice : ""}
                                </Text>
                              </IndexTable.Cell>
                            );
                          }

                          if (!!head.customeField && head.fieldMatch === "discount") {
                            return (
                              <IndexTable.Cell key={j}>
                                <Text as="p" variant="bodySm">
                                  ${x ? x.discount?.toFixed(2) : ""}
                                </Text>
                              </IndexTable.Cell>
                            );
                          }

                          if (!!head.customeField && head.fieldMatch === "deliveryFee") {
                            return (
                              <IndexTable.Cell key={j}>
                                <Text as="p" variant="bodySm" tone="critical">
                                  {x
                                    ? `(${
                                        x.status === "DELIVERED" || x.status === "INSTORE_PURCHASE"
                                          ? "$" + x.deliveryFee
                                          : "--"
                                      })`
                                    : ""}
                                </Text>
                              </IndexTable.Cell>
                            );
                          }

                          if (!!head.customeField && head.fieldMatch === "handlingFee") {
                            return (
                              <IndexTable.Cell key={j}>
                                <Text as="p" variant="bodySm" tone="critical">
                                  {x
                                    ? `(${
                                        x.status === "DELIVERED" || x.status === "INSTORE_PURCHASE"
                                          ? "$" + x.handlingFee
                                          : "--"
                                      })`
                                    : ""}
                                </Text>
                              </IndexTable.Cell>
                            );
                          }

                          if (!!head.customeField && head.fieldMatch === "commission") {
                            return (
                              <IndexTable.Cell key={j}>
                                <Text as="p" variant="bodySm" tone="critical">
                                  {x
                                    ? `(${
                                        x.status === "DELIVERED" || x.status === "INSTORE_PURCHASE"
                                          ? "$" + x.commission
                                          : "--"
                                      })`
                                    : ""}
                                </Text>
                              </IndexTable.Cell>
                            );
                          }

                          if (!!head.customeField && head.fieldMatch === "netSale") {
                            return (
                              <IndexTable.Cell key={j}>
                                <Text as="p" variant="bodySm" tone="success">
                                  {x
                                    ? `${
                                        x.status === "DELIVERED" || x.status === "INSTORE_PURCHASE"
                                          ? "$" +
                                            (x.fullPrice! - (x.deliveryFee! + x.handlingFee! + x.commission!)).toFixed(
                                              2,
                                            )
                                          : "--"
                                      }`
                                    : ""}
                                </Text>
                              </IndexTable.Cell>
                            );
                          }

                          if (!!head.customeField && head.fieldMatch === "log") {
                            return (
                              <IndexTable.Cell key={j}>
                                <div>
                                  {x?.log?.map((log: any) => {
                                    return (
                                      <small>
                                        {log.description} on {log.date}
                                        <br />
                                      </small>
                                    );
                                  })}
                                </div>
                              </IndexTable.Cell>
                            );
                          }

                          return (
                            <IndexTable.Cell key={j}>
                              <Text as="p" variant="bodySm">
                                {x ? (x as any)[head.fieldMatch] : ""}
                              </Text>
                            </IndexTable.Cell>
                          );
                        })}
                      </IndexTable.Row>
                    );
                  })}
              </IndexTable>
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </PageLayout>
  );

  function disambiguateLabel(key: string, value: string | any[]): string {
    switch (key) {
      case "moneySpent":
        return `Money spent is between $${value[0]} and $${value[1]}`;
      case "taggedWith":
        return `Tagged with ${value}`;
      case "accountStatus":
        return (value as string[]).map((val) => `Customer ${val}`).join(", ");
      case "show":
        return `Show Column: ${(value as string[]).length}`;
      case "group":
        return `Group By: ${value}`;
      default:
        return value as string;
    }
  }

  function isEmpty(value: string | any[]) {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === "" || value == null;
    }
  }
}
